import React, { useContext, useEffect, useState } from "react";
import { useWhitelabel } from "../../contexts/WhitelabelContext";
import NotyfContext from "../../contexts/NotyfContext";
import { useDispatch } from "react-redux";
import useWindowSize from "../../hooks/useWindowSize";
import * as Yup from "yup";
import { Button, Form, Modal, Offcanvas } from "react-bootstrap";
import { Formik } from "formik";
import {
  createCustomField,
  updateCustomField,
} from "../../redux/slices/customFields";
import { useTranslation } from "react-i18next";
import useDeviceType from "../../hooks/useDeviceType";

const EditCustomField = ({
  showEditCustomFieldModal,
  setShowEditCustomFieldModal,
  customFieldToEdit,
}) => {
  const { backgroundColor, fontColor } = useWhitelabel();
  const notyf = useContext(NotyfContext);
  const dispatch = useDispatch();
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const [internalShow, setInternalShow] = useState(showEditCustomFieldModal);
  const [fadeOut, setFadeOut] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (showEditCustomFieldModal && !isDesktop) {
      setFadeOut(false);
      setInternalShow(true);
    }
  }, [showEditCustomFieldModal]);

  const handleHide = () => {
    setFadeOut(true);
    setTimeout(() => {
      setInternalShow(false);
      setShowEditCustomFieldModal(false);
    }, 300);
  };

  const initialValues = {
    displayName: customFieldToEdit?.displayName ?? "",
    type: customFieldToEdit?.type ?? "",
  };

  const validation = Yup.object().shape({
    displayName: Yup.string().required("Required"),
    type: Yup.string().required("Required"),
  });

  if (isDesktop) {
    return (
      <Modal
        show={showEditCustomFieldModal}
        onHide={() => setShowEditCustomFieldModal(false)}
        centered
        backdrop="static"
        scrollable
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("customFields.edit")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={initialValues}
            validationSchema={validation}
            onSubmit={async (values, { setValues }) => {
              await dispatch(
                updateCustomField({ id: customFieldToEdit.id, data: values })
              )
                .then(() => {
                  notyf.open({
                    type: "success",
                    message: "Custom Field updated successfully",
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .catch((error) => {
                  notyf.open({
                    type: "danger",
                    message: error,
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .finally(() => {
                  setValues(initialValues);
                  setShowEditCustomFieldModal(false);
                });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setValues,
            }) => {
              return (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("customFields.name")}</Form.Label>
                    <Form.Control
                      type="text"
                      name="displayName"
                      value={values.displayName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={Boolean(
                        touched.displayName && errors.displayName
                      )}
                    />
                    {!!touched.displayName && (
                      <Form.Control.Feedback type="invalid">
                        {errors.displayName}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("customFields.type")}</Form.Label>
                    <Form.Select
                      name="type"
                      value={values.type}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={Boolean(
                        touched.displayName && errors.displayName
                      )}
                    >
                      <option value="">{t("customFields.selectType")}</option>
                      <option value="text">{t("customFields.text")}</option>
                      <option value="number">{t("customFields.number")}</option>
                    </Form.Select>
                    {!!touched.type && (
                      <Form.Control.Feedback type="invalid">
                        {errors.type}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                  <Modal.Footer>
                    <Button
                      variant="primary"
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                        "--dynamic-font-color": fontColor,
                        border: "none",
                      }}
                      onClick={() => {
                        setValues(initialValues);
                        setShowEditCustomFieldModal(false);
                      }}
                    >
                      {t("customFields.cancel")}
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                        "--dynamic-font-color": fontColor,
                        border: "none",
                      }}
                    >
                      {t("customFields.submit")}
                    </Button>
                  </Modal.Footer>
                </Form>
              );
            }}
          </Formik>
        </Modal.Body>
      </Modal>
    );
  } else {
    return (
      <Offcanvas
        show={internalShow}
        placement="bottom"
        backdrop="static"
        scrollable={true}
        className={`fullscreen-offcanvas ${fadeOut ? "fade-out" : ""}`}
        onHide={handleHide}
      >
        <Offcanvas.Header closeButton className="fullscreen-offcanvas-header">
          <Offcanvas.Title>{t("customFields.edit")}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="fullscreen-offcanvas-body">
          <Formik
            initialValues={initialValues}
            validationSchema={validation}
            onSubmit={async (values, { setValues }) => {
              await dispatch(
                updateCustomField({ id: customFieldToEdit.id, data: values })
              )
                .then(() => {
                  notyf.open({
                    type: "success",
                    message: "Custom Field updated successfully",
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .catch((error) => {
                  notyf.open({
                    type: "danger",
                    message: error,
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .finally(() => {
                  setValues(initialValues);
                  setShowEditCustomFieldModal(false);
                });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setValues,
            }) => {
              return (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("customFields.name")}</Form.Label>
                    <Form.Control
                      type="text"
                      name="displayName"
                      value={values.displayName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={Boolean(
                        touched.displayName && errors.displayName
                      )}
                    />
                    {!!touched.displayName && (
                      <Form.Control.Feedback type="invalid">
                        {errors.displayName}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("customFields.type")}</Form.Label>
                    <Form.Select
                      name="type"
                      value={values.type}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={Boolean(
                        touched.displayName && errors.displayName
                      )}
                    >
                      <option value="">{t("customFields.selectType")}</option>
                      <option value="text">{t("customFields.text")}</option>
                      <option value="number">{t("customFields.number")}</option>
                    </Form.Select>
                    {!!touched.type && (
                      <Form.Control.Feedback type="invalid">
                        {errors.type}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                  <div className="d-flex justify-content-center flex-column mt-3">
                    <Button
                      variant="primary"
                      type="submit"
                      className="mb-3"
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                        "--dynamic-font-color": fontColor,
                        border: "none",
                        padding: "10px",
                      }}
                    >
                      {t("customFields.submit")}
                    </Button>
                    <Button
                      variant="primary"
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                        "--dynamic-font-color": fontColor,
                        border: "none",
                        padding: "10px",
                      }}
                      onClick={() => {
                        setValues(initialValues);
                        setShowEditCustomFieldModal(false);
                      }}
                    >
                      {t("customFields.cancel")}
                    </Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </Offcanvas.Body>
      </Offcanvas>
    );
  }
};

export default EditCustomField;
